import type { SheetDraft, SheetRegion } from '../components/project-drawings/drawingSheetDetection';
export interface DrawingSetReviewRecord { drafts: Record<number, SheetDraft>; region: SheetRegion; numberRegion?: SheetRegion; titleRegion?: SheetRegion; rules: Record<string, string>; updatedAt: number }
// Save changed pages independently: scanning 5000 sheets must not serialize
// the whole set after every page. Scope by user/project/immutable source.
const DATABASE = 'forxa-drawing-set-review-v1';
const written = new Map<string, Record<number, SheetDraft>>();
let generation = 0;
async function database() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 2);
    request.onupgradeneeded = () => { for (const name of ['reviews', 'pages']) if (!request.result.objectStoreNames.contains(name)) request.result.createObjectStore(name); };
    request.onsuccess = () => { request.result.onversionchange = () => request.result.close(); resolve(request.result); };
    request.onerror = () => reject(request.error);
  });
}
export async function readDrawingSetReview(key: string): Promise<DrawingSetReviewRecord | undefined> {
  const db = await database();
  try { return await new Promise((resolve, reject) => {
    const tx = db.transaction(['reviews', 'pages']); const config = tx.objectStore('reviews').get(key);
    const pages = tx.objectStore('pages').getAll(IDBKeyRange.bound(`${key}:`, `${key}:\uffff`));
    tx.oncomplete = () => { const record = config.result as DrawingSetReviewRecord | undefined; if (!record) { resolve(undefined); return; } const drafts = { ...(record.drafts || {}) }; (pages.result as { number: number; draft: SheetDraft }[]).forEach((page) => { drafts[page.number] = page.draft; }); written.set(key, drafts); resolve({ ...record, drafts }); };
    tx.onerror = () => reject(tx.error); tx.onabort = () => reject(tx.error);
  }); }
  finally { db.close(); }
}
export async function writeDrawingSetReview(key: string, record: DrawingSetReviewRecord) {
  const started = generation; const db = await database();
  try {
    if (started !== generation) return;
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(['reviews', 'pages'], 'readwrite'); const { drafts, ...config } = record;
      tx.objectStore('reviews').put(config, key); const previous = written.get(key) || {};
      Object.entries(drafts).forEach(([number, draft]) => { if (previous[Number(number)] !== draft) tx.objectStore('pages').put({ number: Number(number), draft }, `${key}:${number}`); });
      tx.oncomplete = () => { written.set(key, drafts); resolve(); }; tx.onerror = () => reject(tx.error); tx.onabort = () => reject(tx.error);
    });
  }
  finally { db.close(); }
}
export async function clearDrawingSetReview() {
  generation++; written.clear();
  if (typeof indexedDB === 'undefined') return;
  await new Promise<void>((resolve, reject) => { const request = indexedDB.deleteDatabase(DATABASE); request.onsuccess = () => resolve(); request.onerror = () => reject(request.error); request.onblocked = () => reject(new Error('Review storage is busy')); });
}
